import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { DocumentFile } from '../../types';

export interface Tab {
  file: DocumentFile;
  content: string;
  isDirty: boolean;
  lastSavedContent: string;
}

interface TabBarProps {
  tabs: Tab[];
  activeTabId: string | null;
  onTabClick: (fileId: string) => void;
  onTabClose: (fileId: string, event: React.MouseEvent) => void;
}

export const TabBar: React.FC<TabBarProps> = ({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
}) => {
  if (tabs.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
      <div className="flex items-center min-w-full">
        {tabs.map((tab) => {
          const isActive = tab.file.id === activeTabId;
          const displayName = tab.file.is_main_tex 
            ? `${tab.file.name} (main)`
            : tab.file.name;

          return (
            <div
              key={tab.file.id}
              className={clsx(
                'group flex items-center gap-2 px-4 py-2 border-r border-gray-200 dark:border-gray-700',
                'cursor-pointer transition-colors min-w-fit',
                'hover:bg-gray-100 dark:hover:bg-gray-700',
                isActive
                  ? 'bg-white dark:bg-gray-900 border-b-2 border-b-blue-500'
                  : 'bg-gray-50 dark:bg-gray-800'
              )}
              onClick={() => onTabClick(tab.file.id)}
            >
              {/* Unsaved indicator */}
              {tab.isDirty && (
                <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
              )}
              
              {/* File name */}
              <span
                className={clsx(
                  'text-sm font-medium truncate max-w-[200px]',
                  isActive
                    ? 'text-gray-900 dark:text-gray-100'
                    : 'text-gray-600 dark:text-gray-400'
                )}
                title={tab.file.file_path}
              >
                {displayName}
              </span>

              {/* Close button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTabClose(tab.file.id, e);
                }}
                className={clsx(
                  'opacity-0 group-hover:opacity-100 transition-opacity',
                  'p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600',
                  'flex-shrink-0'
                )}
                title="Close tab"
              >
                <XMarkIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
